import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { store } from '../store';
import { obterTokenFunil, rastrearEventoFunil } from './rastreamentoFunil';
import { estaEmPeriodoDeTeste } from './seguranca';

/**
 * Comportamento das contas em período de teste ("testantes"): usa a mesma fila do funil, mas só
 * registra quando o token é de teste, para não gerar volume com clientes pagantes. Nunca envia dado
 * pessoal (nome, CPF, placa, e-mail, conteúdo de contrato) nas propriedades, e nunca lança erro.
 */

const obterToken = (): string | null => {
    try {
        const doStore = store.getState().token;
        if (doStore) return String(doStore).replace(/"/g, '');
    } catch { /* cai no armazenado */ }
    return obterTokenFunil();
};

export const contaEmTeste = (): boolean => {
    try {
        const token = obterToken();
        return Boolean(token) && estaEmPeriodoDeTeste(token!);
    } catch {
        return false;
    }
};

export const rastrearTeste = (evento: string, propriedades?: Record<string, unknown>): void => {
    try {
        if (!contaEmTeste()) return;
        rastrearEventoFunil(evento, propriedades);
    } catch { /* telemetria nunca afeta a UX */ }
};

/** Segundos inteiros desde um Date.now() anterior. */
export const segundosDesde = (inicio: number): number => Math.max(0, Math.round((Date.now() - inicio) / 1000));

const PARECE_GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PARECE_NUMERO = /^\d+$/;
// Sequências longas com dígitos (hash, token, id codificado) também viram :id.
const PARECE_ID_LONGO = /^(?=.*\d)[0-9a-z_-]{16,}$/i;

/** Rota sem query string, com os segmentos que parecem id trocados por ":id". */
export const normalizarRota = (caminho: string): string => {
    const semQuery = (caminho || '/').split(/[?#]/)[0];
    const normalizada = semQuery
        .split('/')
        .map(s => (PARECE_GUID.test(s) || PARECE_NUMERO.test(s) || PARECE_ID_LONGO.test(s) ? ':id' : s))
        .join('/')
        .replace(/\/+$/, '');
    return normalizada || '/';
};

let ultimaRotaVista = '';

/** "pagina_vista" a cada troca de rota, sem repetir a mesma rota em sequência. */
export const rastrearPaginaVista = (caminho: string): void => {
    try {
        const rota = normalizarRota(caminho);
        if (rota === ultimaRotaVista) return;
        if (!contaEmTeste()) return;
        ultimaRotaVista = rota;
        rastrearEventoFunil('pagina_vista', { rota });
    } catch { /* telemetria nunca afeta a UX */ }
};

/**
 * Colocado uma vez dentro do Router: registra "pagina_vista" em toda troca de rota, inclusive nas
 * telas fora da moldura do sistema (onboarding, planos, checkout).
 */
export function RastreadorDePaginas(): null {
    const { pathname } = useLocation();
    useEffect(() => { rastrearPaginaVista(pathname); }, [pathname]);
    return null;
}
